import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import type { SortingRule } from 'react-table'
import { useIntl } from 'react-intl'
import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash/debounce'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { LoaderOverlay, SpinnerCentered } from 'components/Loader'
import { MobileSortBy } from 'components/Table'
import { getSortOptions } from 'components/Table/utils'

import {
  fetchDomainJobsWithHomeThunk,
  fetchDomainJobsWithHomeCountThunk,
} from 'features/domains/slice'
import {
  domainJobsWithHomeSelector,
  domainJobsWithHomeCountSelector,
  domainJobsWithHomeDataSelector,
  selectedNavDomainSelector,
} from 'features/domains/selectors'
import { DomainJobsHomesTable } from 'features/domains/components'

import { JOBS_HOMES_HEADERS } from './consts'
import { jobsTablesMessages } from '../DomainJobsHomesTable/messages'
import type { JobWithHomeType } from 'features/domains/types'
import { DomainJobsListWrapper } from 'features/domains/styles'
import useCurrentSelectedDomainId from 'features/DomainsSelector/hooks'

interface DomainJobsHomesProps {
  searchValue?: string
}

const apiSort = {
  [JOBS_HOMES_HEADERS.STATUS]: 'is_online',
  [JOBS_HOMES_HEADERS.UPDATED]: 'job_updated',
  [JOBS_HOMES_HEADERS.NAME]: 'home_name',
  [JOBS_HOMES_HEADERS.SOFT_VERSION]: 'software_version',
  [JOBS_HOMES_HEADERS.SUBSCR_ENDS]: 'sub_expires',
  [JOBS_HOMES_HEADERS.LAST_BACKUP]: 'last_backup',
}

function DomainJobsHomes(props: DomainJobsHomesProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const domainId = useCurrentSelectedDomainId()
  const domainLoaded = useAppSelector(selectedNavDomainSelector.isFinished)
  const domainIsLoading = useAppSelector(selectedNavDomainSelector.isFetching)
  const domainJobsLoading = useAppSelector(
    domainJobsWithHomeDataSelector.isFetching
  )

  const domainJobsWithHome = useAppSelector(domainJobsWithHomeSelector)
  const domainJobsWithHomeCount = useAppSelector(
    domainJobsWithHomeCountSelector
  ) as number

  const domainExists = domainLoaded && !domainIsLoading

  const [pageCount, setPageCount] = useState(0)
  const [normalizedSearchValue, setNormalizedSearchValue] = useState('')

  const debounceSetNormalizedSearchValue = useMemo(
    () => _debounce(setNormalizedSearchValue, 400),
    []
  )

  const fetchDomainHomes = useCallback(() => {
    if (domainId) {
      dispatch(
        fetchDomainJobsWithHomeThunk(domainId, {
          sort: 'is_online.ASC',
          page: 0,
          count: 10,
        })
      )
      dispatch(fetchDomainJobsWithHomeCountThunk(domainId))
    }
  }, [dispatch, domainId])

  useEffect(() => {
    if (domainExists && domainId) {
      fetchDomainHomes()
    }
  }, [domainExists, domainId, fetchDomainHomes])

  useEffect(() => {
    if (props.searchValue && props.searchValue.length > 2) {
      debounceSetNormalizedSearchValue(props.searchValue)
    } else {
      debounceSetNormalizedSearchValue('')
    }
  }, [props.searchValue, debounceSetNormalizedSearchValue])

  useEffect(() => {
    if (domainId) {
      dispatch(
        fetchDomainJobsWithHomeCountThunk(domainId, {
          filter: normalizedSearchValue,
        })
      )
    }
  }, [dispatch, domainId, normalizedSearchValue])

  const headers = useMemo(
    () => ({
      status: {
        label: ' ',
        id: JOBS_HOMES_HEADERS.STATUS,
      },
      name: {
        label: formatMessage(jobsTablesMessages.homeName),
        id: JOBS_HOMES_HEADERS.NAME,
      },
      updated: {
        label: formatMessage(jobsTablesMessages.updated),
        id: JOBS_HOMES_HEADERS.UPDATED,
      },
      softwareVersion: {
        label: formatMessage(jobsTablesMessages.softwareVersion),
        id: JOBS_HOMES_HEADERS.SOFT_VERSION,
      },
      subscriptionPlan: {
        label: formatMessage(jobsTablesMessages.subscriptionPlan),
        id: JOBS_HOMES_HEADERS.SUBSCR_PLAN,
        disableSortBy: true,
      },
      subscriptionEnds: {
        label: formatMessage(jobsTablesMessages.subscriptionEnds),
        id: JOBS_HOMES_HEADERS.SUBSCR_ENDS,
        disableSortBy: true,
      },
      lastBackup: {
        label: formatMessage(jobsTablesMessages.lastBackup),
        id: JOBS_HOMES_HEADERS.LAST_BACKUP,
      },
    }),
    [formatMessage]
  )
  const headersMobile = useMemo(() => {
    let newHeaders = _cloneDeep(headers)
    newHeaders.status.label = formatMessage(jobsTablesMessages.status)
    return newHeaders
  }, [formatMessage, headers])

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])
  const [sortBy, setSortBy] = useState<SortingRule<JobWithHomeType>>(
    sortOptions[0]
  )

  const fetchData = useCallback(
    ({ pageSize, pageIndex, sort }) => {
      if (domainJobsWithHomeCount === undefined) {
        return
      }
      setPageCount(Math.ceil((domainJobsWithHomeCount as number) / pageSize))
      dispatch(
        fetchDomainJobsWithHomeThunk(domainId, {
          // @ts-ignore
          sort: `${apiSort[sort.id]}${sort.desc ? '.desc' : '.asc'}`,
          filter: normalizedSearchValue,
          page: pageIndex,
          count: pageSize,
        })
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, domainId, domainJobsWithHomeCount]
  )

  if (domainIsLoading) {
    return <SpinnerCentered />
  }

  return (
    <>
      {domainExists ? (
        <DomainJobsListWrapper>
          <>
            <MobileSortBy<JobWithHomeType>
              headers={headersMobile}
              sortOptions={sortOptions}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
            <DomainJobsHomesTable
              data={domainJobsWithHome ?? []}
              fetchData={fetchData}
              loading={domainJobsLoading}
              headers={headers}
              defaultSortBy={sortOptions[0]}
              sortBy={sortBy}
              setSortBy={setSortBy}
              pageCount={pageCount}
              itemCount={domainJobsWithHomeCount}
            />
          </>
        </DomainJobsListWrapper>
      ) : (
        <>
          <LoaderOverlay />
        </>
      )}
    </>
  )
}

export default memo(DomainJobsHomes)
